import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import ModalPop from 'components/sales/modal/modal';
import FormSection from 'components/sales/formSection/formSection';
import PeriodSummary from './PeriodSummary';
import './salesTargetsListing.css';

const getTodayYmdLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isValidDate = (value) => {
  if (!value) return false;
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(value)) return false;
  const date = new Date(`${value}T12:00:00`);
  return !Number.isNaN(date.getTime());
};

const isNotFutureDate = (value, maxYmd) => {
  if (!value || !maxYmd) return false;
  return value <= maxYmd;
};

const getErrorMessage = (err) => {
  const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
  return msg && typeof msg === 'string' ? msg : 'Something went wrong. Please try again.';
};

const UpdateEndDatePopup = ({
  show,
  onClose,
  onSubmit,
  onSuccess,
  currentPeriodRange,
  periodMonthYearLabel,
  periodWeeks,
}) => {
  const [endDate, setEndDate] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  useEffect(() => {
    if (show) {
      setEndDate(getTodayYmdLocal());
      setTouched(false);
      setSubmitError('');
      setSubmitSuccess('');
    }
  }, [show]);

  const handleHide = () => {
    setEndDate('');
    setTouched(false);
    setSubmitError('');
    setSubmitSuccess('');
    onClose();
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValidDate(endDate)) return;
    if (!isNotFutureDate(endDate, getTodayYmdLocal())) return;

    setSubmitError('');
    setSubmitSuccess('');
    setIsSubmitting(true);
    try {
      if (typeof onSubmit === 'function') {
        await onSubmit(endDate);
      }
      setSubmitSuccess('End date updated successfully.');
      if (typeof onSuccess === 'function') {
        try {
          await onSuccess();
        } catch {
          /* refresh failed; primary update still succeeded */
        }
      }
    } catch (err) {
      setSubmitError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const maxDate = getTodayYmdLocal();
  const dateOk = isValidDate(endDate) && isNotFutureDate(endDate, maxDate);
  const showError = touched && !dateOk;

  return (
    <ModalPop
      title="Update End Date"
      modalSize="compact"
      centered
      headerClassName="set-targets-modal-header"
      titleClassName="modal-title set-targets-modal-title"
      show={show}
      handleModalHide={handleHide}
      keyboard
      backdrop
    >
      <FormSection isLoading={isSubmitting}>
        <div className="set-targets-popup set-targets-update-end-body">
          <PeriodSummary
            monthLabel={periodMonthYearLabel}
            periodRangeLabel={currentPeriodRange}
            weeks={periodWeeks}
          />

          {submitError && (
            <div className="alert alert-danger set-targets-popup-error" role="alert">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="alert alert-success set-targets-popup-error" role="alert">
              {submitSuccess}
            </div>
          )}

          <Form.Group controlId="salesTargetsEndDate" className="set-targets-end-date-field mb-0">
            <div className="set-targets-end-date-group">
              <Form.Label className="set-targets-end-date-label-block">
                <span className="set-targets-end-date-label-main">End date</span>
                <span className="set-targets-end-date-label-hint">(today or earlier)</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                max={maxDate}
                className="set-targets-end-date-input"
                onChange={(e) => setEndDate(e.target.value)}
                onBlur={() => setTouched(true)}
              />
            </div>
            {showError && (
              <div className="text-danger mt-2 small set-targets-end-date-error">
                {!isValidDate(endDate)
                  ? 'Please enter a valid date.'
                  : 'End date cannot be in the future.'}
              </div>
            )}
          </Form.Group>

          <div className="set-targets-update-end-actions">
            <Button
              className="btn btn-primary btn-sm set-targets-end-date-submit"
              onClick={handleSubmit}
              disabled={isSubmitting || !dateOk}
            >
              {isSubmitting ? 'Updating...' : 'Update end date'}
            </Button>
          </div>
        </div>
      </FormSection>
    </ModalPop>
  );
};

UpdateEndDatePopup.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  currentPeriodRange: PropTypes.string,
  periodMonthYearLabel: PropTypes.string,
  periodWeeks: PropTypes.arrayOf(
    PropTypes.shape({
      week_start: PropTypes.string,
      week_end: PropTypes.string,
    })
  ),
};

UpdateEndDatePopup.defaultProps = {
  onSuccess: undefined,
  currentPeriodRange: '',
  periodMonthYearLabel: '',
  periodWeeks: [],
};

export default UpdateEndDatePopup;
